import { mlsProviderIds } from 'constants/mlsProviderIds';
import { LOCALE_COMMON_PATHS } from 'types/locales';

export interface FormatMlsNumberOptions {
  mlsNumber: string;
  /**
   * Feed the listing came from. Some MLSs mandate their own prefix instead of
   * the generic `MLS®` label — see PROVIDER_MLS_NUMBER_PREFIXES.
   */
  providerId?: number | null;
}

/** Default label. `MLS®` is a CREA trademark, used here as the generic prefix. */
export const DEFAULT_MLS_NUMBER_PREFIX = 'MLS®';

/**
 * Per-feed overrides for the listing-number prefix.
 *
 * FMLS IDX rule 13.7 requires FMLS listing numbers to start with `FMLS#`, so
 * the generic `MLS®` prefix is not compliant for that feed.
 */
const PROVIDER_MLS_NUMBER_PREFIXES: Record<number, string> = {
  [mlsProviderIds.expportal_fmls]: 'FMLS#',
};

/**
 * Locale keys for the detail-table row label, per feed. Detail tables render a
 * label/value pair rather than a single formatted string, so the prefix has to
 * live in the label to keep the number itself starting with `FMLS#`.
 */
const PROVIDER_MLS_NUMBER_LABEL_KEYS: Record<number, { keyFacts: string; description: string }> = {
  [mlsProviderIds.expportal_fmls]: {
    keyFacts: LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_FMLS_NUMBER,
    description: LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_FMLS_NUMBER,
  },
};

/**
 * The prefix that precedes a listing number for a given feed.
 */
export function getMlsNumberPrefix(providerId?: number | null): string {
  if (providerId == null) {
    return DEFAULT_MLS_NUMBER_PREFIX;
  }

  return PROVIDER_MLS_NUMBER_PREFIXES[providerId] ?? DEFAULT_MLS_NUMBER_PREFIX;
}

/**
 * Locale key for the "MLS® Number" row label in the key facts table.
 */
export function getKeyFactsMlsNumberLabelKey(providerId?: number | null): string {
  if (providerId == null) {
    return LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_MLS_NUMBER;
  }

  return PROVIDER_MLS_NUMBER_LABEL_KEYS[providerId]?.keyFacts
    ?? LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_MLS_NUMBER;
}

/**
 * Locale key for the "MLS® Number" row label in the description details table.
 */
export function getDescriptionMlsNumberLabelKey(providerId?: number | null): string {
  if (providerId == null) {
    return LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_MLS_NUMBER;
  }

  return PROVIDER_MLS_NUMBER_LABEL_KEYS[providerId]?.description
    ?? LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_MLS_NUMBER;
}

/**
 * Formats an MLS number with its feed-appropriate label prefix.
 */
export function formatMlsNumber({ mlsNumber, providerId }: FormatMlsNumberOptions): string {
  return `${getMlsNumberPrefix(providerId)} ${mlsNumber}`;
}

/**
 * Compact form used where space is tight (map pin popovers): `MLS®#1234567`.
 * Prefixes that already end in `#` are not given a second one.
 */
export function formatMlsNumberCompact({ mlsNumber, providerId }: FormatMlsNumberOptions): string {
  const prefix = getMlsNumberPrefix(providerId);
  return prefix.endsWith('#') ? `${prefix}${mlsNumber}` : `${prefix}#${mlsNumber}`;
}
